/**
 * Clients — the businesses the firm audits. They never sign in.
 *
 * One list for every role (`/clients`; the old `/intake` redirects here). A
 * client opens on its module page first (`ClientModulesPage`), so the list is
 * every client of the firm (each is in every module). The Documents
 * and Audits counts are columns each role sees only for the half it can use.
 * Only an administrator adds a client, and the API enforces that too.
 */

import {
	flowAccess,
	mergeClientLists,
	modulePagePath,
	tallyText,
} from '../features/modules/flow';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	Box,
	Button,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import { FolderCard } from '../features/shared/FolderCard';
import {
	DataTable,
	FilterBar,
	useFullScreen,
	type Column,
} from '../features/shared/tables';
import { count } from '../utils/format';
import { ApiError } from '../api/errors';
import { useToast } from '../provider/toast';
import { useEveryModuleClientsQuery } from '../queries/clients';
import { useCreateClientMutation } from '../queries/firm';
import { useAuth } from '../provider/auth';
import { PageHeader } from '../provider/pageTitle';
import { Card, Empty, ErrorNotice } from '../shared/ui';
import FormTextField from '../shared/components/form/text-field';
import FormErrorBox from '../shared/components/form/error';
import { ErrorState, LoadingState } from '../features/shared/states';
import { addClientFormSchema, type AddClientForm } from '../schemas/forms';
import type { ClientRecord } from '../schemas';

/** Two letters for the card's mark: first and last word ("EP"). */
function initials(name: string): string {
	const words = name.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return '—';
	const last =
		words.length > 1 ? words[words.length - 1]![0]! : (words[0]![1] ?? '');
	return (words[0]![0]! + last).toUpperCase();
}

type View = 'list' | 'cards';
const VIEW_KEY = 'reconcilio.clients.view';

/* A per-browser convenience; private windows and blocked storage fall back
   to the list. */
function readView(): View {
	try {
		return localStorage.getItem(VIEW_KEY) === 'cards' ? 'cards' : 'list';
	} catch {
		return 'list';
	}
}
function saveView(view: View) {
	try {
		localStorage.setItem(VIEW_KEY, view);
	} catch {
		/* ignore */
	}
}

export default function Clients() {
	const { permissions } = useAuth();
	const navigate = useNavigate();
	const [view, setView] = useState<View>(readView);
	/* Each role sees the counts of the halves it can use. */
	const access = flowAccess(permissions);
	const [search, setSearch] = useState('');
	/* Every client, whichever modules it is in: there is no all-modules list,
     so each module's is read and they are merged (counts summed). */
	const everyModule = useEveryModuleClientsQuery();
	const failed = everyModule.error;
	/* Full screen sits in the filter row, beside the view toggle, so the table
     card holds the table alone. */
	const fullScreen = useFullScreen('Clients');

	const rows = mergeClientLists(everyModule.lists);
	const term = search.trim().toLowerCase();
	const visible = term
		? rows.filter((row) =>
				`${row.name} ${row.code} ${row.gstin ?? ''}`
					.toLowerCase()
					.includes(term),
			)
		: rows;
	/* A–Z by name, as the table used to open. */
	const sorted = [...visible].sort((x, y) => x.name.localeCompare(y.name));
	/* A client opens on its module page, which carries the module from there. */
	const hrefOf = (id: string) => modulePagePath(id);
	const columns: Array<Column<ClientRecord>> = [
		{
			key: 'name',
			label: 'Client',
			width: 280,
			sortValue: (row) => row.name,
			render: (row) => <span className="b">{row.name}</span>,
		},
		{
			key: 'code',
			label: 'Code',
			width: 120,
			sortValue: (row) => row.code,
			render: (row) => (
				<span style={{ fontFamily: 'var(--font-mono)' }}>
					{row.code}
				</span>
			),
		},
		{
			key: 'gstin',
			label: 'GSTIN',
			width: 180,
			sortValue: (row) => row.gstin ?? '',
			render: (row) => row.gstin ?? '—',
		},

		...(access.documents
			? [
					{
						key: 'documents',
						label: 'Data Files',
						num: true,
						width: 110,
						sortValue: (row: ClientRecord) => row.files ?? 0,
						render: (row: ClientRecord) => count(row.files ?? 0),
					},
				]
			: []),
		...(access.audits
			? [
					{
						key: 'audits',
						label: 'Audits',
						num: true,
						width: 90,
						sortValue: (row: ClientRecord) => row.audits ?? 0,
						render: (row: ClientRecord) => count(row.audits ?? 0),
					},
				]
			: []),
	];

	return (
		<Box className="stack">
			<PageHeader
				title="Clients"
				actions={
					permissions?.manageClients ? (
						<Button
							component={Link}
							variant="contained"
							to="/clients/new"
						>
							Add client
						</Button>
					) : undefined
				}
			/>

			{everyModule.isPending ? (
				<LoadingState label="Loading clients…" />
			) : failed ? (
				<ErrorState error={failed} onRetry={everyModule.refetch} />
			) : (
				<>
					<FilterBar
						specs={[
							{
								key: 'q',
								label: 'Search',
								kind: 'search',
								placeholder: 'Client name, code or GSTIN',
							},
						]}
						values={{ q: search }}
						onChange={(_key, value) => setSearch(value)}
						onClear={() => setSearch('')}
						resultNote={
							term
								? `${count(sorted.length)} of ${count(rows.length)}`
								: `${count(rows.length)} ${rows.length === 1 ? 'client' : 'clients'}`
						}
						actions={
							<>
								{view === 'list' &&
									sorted.length > 0 &&
									fullScreen.button}
								<ToggleButtonGroup
									exclusive
									size="small"
									value={view}
									aria-label="View"
									onChange={(_event, next: View | null) => {
										if (!next) return;
										setView(next);
										saveView(next);
									}}
									sx={{
										'& .MuiToggleButton-root': { p: '4px' },
									}}
								>
									<ToggleButton
										value="list"
										aria-label="List"
									>
										<ViewListOutlinedIcon fontSize="small" />
									</ToggleButton>
									<ToggleButton
										value="cards"
										aria-label="Cards"
									>
										<GridViewOutlinedIcon fontSize="small" />
									</ToggleButton>
								</ToggleButtonGroup>
							</>
						}
					/>

					{sorted.length === 0 ? (
						<Card>
							{term ? (
								<Typography className="mut">
									No client matches that search.
								</Typography>
							) : permissions?.manageClients ? (
								<Empty
									title="No clients yet"
									action={
										<Button
											component={Link}
											variant="contained"
											to="/clients/new"
										>
											Add client
										</Button>
									}
								>
									Add the first business your firm audits.
								</Empty>
							) : (
								<Typography className="mut">
									No clients yet. An administrator adds
									clients.
								</Typography>
							)}
						</Card>
					) : view === 'list' ? (
						<Box className="card">
							<Box className="card-bd flush">
								<DataTable
									rows={sorted}
									columns={columns}
									rowKey={(row) => row.pkClientId}
									onRowClick={(row) =>
										navigate(hrefOf(row.pkClientId))
									}
									caption="Clients"
									fullScreen={fullScreen}
									initialSort={{
										key: 'name',
										direction: 'asc',
									}}
								/>
							</Box>
						</Box>
					) : (
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns:
									'repeat(auto-fill, minmax(260px, 1fr))',
								gap: 'var(--s3)',
							}}
						>
							{sorted.map((row) => {
								return (
									<FolderCard
										key={row.pkClientId}
										component={Link}
										href={hrefOf(row.pkClientId)}
										icon={initials(row.name)}
										title={row.name}
										meta={[row.code, row.gstin]
											.filter(Boolean)
											.join(' · ')}
										count={tallyText(
											{
												documents: row.files ?? 0,
												audits: row.audits ?? 0,
											},
											access,
										)}
									/>
								);
							})}
						</Box>
					)}
				</>
			)}
		</Box>
	);
}

// ═══════════════════════════════════════════════════════════════════════════

/**
 * Adding a client — a page, not a drawer, matching the approved build.
 *
 * `POST /clients` (`clientsController.create`) accepts exactly
 * `name, code, erp, gstin` — the approved form's `pan`, `industry`,
 * `contactName` and `contactEmail` have no counterpart on the `Client`
 * model and are dropped rather than collected and silently discarded.
 *
 * A client is in every module. Once added, the client's module page opens.
 */
export function AddClientPage() {
	const navigate = useNavigate();
	const toast = useToast();
	const list = { label: 'Clients', to: '/clients' };
	const createClient = useCreateClientMutation();

	const {
		control,
		handleSubmit,
		setError,
		watch,
		formState: { errors },
	} = useForm<AddClientForm>({
		resolver: zodResolver(addClientFormSchema),
		defaultValues: { name: '', code: '', erp: '', gstin: '' },
	});
	const name = watch('name');
	const code = watch('code');

	const submit = handleSubmit((values) => {
		createClient.mutate(
			{
				...values,
				code: values.code.toUpperCase(),
				gstin: values.gstin.toUpperCase(),
			},
			{
				onSuccess: (client) => {
					toast('ok', 'Client added.');
					navigate(modulePagePath(client.pkClientId));
				},
				onError: (error) => {
					if (!(error instanceof ApiError)) return;
					for (const [field, message] of Object.entries(
						error.fields,
					)) {
						if (field in values)
							setError(field as keyof AddClientForm, { message });
					}
				},
			},
		);
	});

	return (
		<Box className="stack wizard-page">
			<PageHeader
				breadcrumbs={[list]}
				title="Add client"
				subtitle="A business your firm audits. Clients do not sign in."
			/>

			<ErrorNotice error={createClient.error} />

			<Card title="Client details">
				<Box className="stack">
					<Box className="grid g2">
						<Box>
							<FormTextField
								control={control}
								name="name"
								label="Name"
								isRequired
								muiProps={{
									placeholder: 'ABC Pharma Ltd.',
									autoFocus: true,
								}}
							/>
							<FormErrorBox errors={[errors.name]} />
						</Box>
						<Box>
							<FormTextField
								control={control}
								name="code"
								label="Short code"
								isRequired
								muiProps={{
									placeholder: 'ABCPH',
									helperText:
										"Your firm's short reference for this client, e.g. ABCPH",
								}}
							/>
							<FormErrorBox errors={[errors.code]} />
						</Box>
					</Box>
					<Box className="grid g2">
						<Box>
							<FormTextField
								control={control}
								name="gstin"
								label="GSTIN"
								muiProps={{ placeholder: '27AAAAA0000A1Z5' }}
							/>
							<FormErrorBox errors={[errors.gstin]} />
						</Box>
						<Box>
							<FormTextField
								control={control}
								name="erp"
								label="ERP"
								muiProps={{
									placeholder: 'SAP, Tally…',
									helperText:
										'Optional. Where their exports come from, e.g. SAP or Tally',
								}}
							/>
							<FormErrorBox errors={[errors.erp]} />
						</Box>
					</Box>
				</Box>
			</Card>

			<Box className="wizard-foot">
				<Box className="spread">
					<Button component={Link} variant="outlined" to={list.to}>
						Cancel
					</Button>
					<Button
						loading={createClient.isPending}
						type="button"
						variant="contained"
						disabled={
							createClient.isPending ||
							!name.trim() ||
							!code.trim()
						}
						onClick={() => void submit()}
					>
						Add client
					</Button>
				</Box>
			</Box>
		</Box>
	);
}
